import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PhoneVerificationService } from 'src/app/Services/phone-verification.service';
import Swal from 'sweetalert2';
import { getCountriesList } from 'src/app/countriesConfig';

export interface PhoneVerificationDialogData {
  phoneNumber?: string;
  skipable?: boolean;
  title?: string;
  message?: string;
}

@Component({
    selector: 'app-phone-verification-modal',
    templateUrl: './phone-verification-modal.component.html',
    styleUrls: ['./phone-verification-modal.component.css'],
    standalone: false
})
export class PhoneVerificationModalComponent implements OnInit {
  verificationForm: FormGroup;
  step: 'phone' | 'code' = 'phone';
  isLoading = false;
  selectedChannel: 'sms' | 'whatsapp' = 'sms';
  phoneNumber: string = '';
  countdown: number = 0;
  countdownInterval: any;
  canResend = false;
  errorMessage: string = '';
  selectedCountryCode: string = '+1'; // Default fallback
  detectedCountryCode: string = '';
  countries = getCountriesList();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PhoneVerificationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PhoneVerificationDialogData,
    private phoneVerificationService: PhoneVerificationService
  ) {
    this.verificationForm = this.fb.group({
      phoneNumber: [
        '', 
        [Validators.required, Validators.pattern(/^[0-9]{6,14}$/)]
      ],
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });

    // Parse existing phone number if provided
    if (this.data?.phoneNumber) {
      const parsed = this.parsePhoneNumber(this.data.phoneNumber);
      this.selectedCountryCode = parsed.countryCode;
      this.verificationForm.patchValue({ phoneNumber: parsed.number });
    }

    // Disable close on backdrop click unless skipable
    this.dialogRef.disableClose = !this.data?.skipable;
  }

  /**
   * Parse phone number to extract country code and number
   */
  parsePhoneNumber(fullNumber: string): { countryCode: string, number: string } {
    // Remove any spaces or special characters
    const cleaned = fullNumber.replace(/[\s\-\(\)]/g, '');
    
    // Try to match with our country codes
    for (const country of this.countries) {
      if (cleaned.startsWith(country.dialCode)) {
        return {
          countryCode: country.dialCode,
          number: cleaned.substring(country.dialCode.length)
        };
      }
    }
    
    // Default to +1 if no match
    return {
      countryCode: '+1',
      number: cleaned.replace(/^\+/, '')
    };
  }

  /**
   * Handle country code change
   */
  onCountryChange(event: any): void {
    this.selectedCountryCode = event.value;
  }

  /**
   * Get selected country object
   */
  getSelectedCountry() {
    return this.countries.find(c => c.dialCode === this.selectedCountryCode);
  }

  ngOnInit(): void {
    console.log('[PhoneVerificationModal] Initialized with data:', this.data);
    
    // Detect user's country if not already provided via phone number
    if (!this.data?.phoneNumber) {
      this.detectUserCountry();
    }
  }

  /**
   * Detect user's country based on their IP location
   */
  private detectUserCountry(): void {
    // Try to get country from geolocation API
    fetch('https://ipapi.co/json/')
      .then(response => response.json())
      .then(data => {
        if (data && data.country_code) {
          const countryCode = data.country_code.toUpperCase();
          const country = this.countries.find(c => c.code === countryCode);
          
          if (country) {
            this.selectedCountryCode = country.dialCode;
            this.detectedCountryCode = countryCode;
            console.log('[PhoneVerificationModal] Detected country:', country.name, 'with dial code:', country.dialCode);
          } else {
            console.log('[PhoneVerificationModal] Country detected but not in list:', countryCode);
          }
        }
      })
      .catch(error => {
        console.warn('[PhoneVerificationModal] Failed to detect country, using default:', error);
        // Keep the default +1 if detection fails
      });
  }

  /**
   * Send verification code
   */
  sendCode(): void {
    if (this.verificationForm.get('phoneNumber')?.invalid) {
      this.errorMessage = 'Please enter a valid phone number';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    const localNumber = this.verificationForm.get('phoneNumber')?.value;
    this.phoneNumber = this.selectedCountryCode + localNumber;

    this.phoneVerificationService.sendVerificationCode(this.phoneNumber, this.selectedChannel, this.selectedCountryCode, this.getSelectedCountry()?.dialCode)
      .subscribe(
        (response) => {
          this.isLoading = false;
          this.step = 'code';
          this.startCountdown();          
        },
        (error) => {
          this.isLoading = false;
          console.error('[PhoneVerification] Send code error:', error);
          
          // Extract error message from various possible formats
          let errorMessage = 'Failed to send verification code. Please try again.';
          let errorTitle = 'Error';
          
          if (error?.error) {
            if (error.error.message) {
              errorMessage = error.error.message;
            }
            if (error.error.code === 'invalid_phone') {
              errorTitle = 'Invalid Phone Number';
            }
          } else if (error?.message) {
            errorMessage = error.message;
          }
          
          // Display error inline
          this.errorMessage = errorMessage;
        }
      );
  }

  /**
   * Verify the entered code
   */
  verifyCode(): void {
    const code = this.verificationForm.get('code')?.value;
    
    if (!code || code.length !== 6) {
      this.errorMessage = 'Please enter a 6-digit verification code';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.phoneVerificationService.verifyCode(this.phoneNumber, code)
      .subscribe(
        (response) => {
          this.isLoading = false;
          this.stopCountdown();
          // Close dialog and return success
          setTimeout(() => {
            this.dialogRef.close({ verified: true, phoneNumber: this.phoneNumber });
          }, 2000);
        },
        (error) => {
          this.isLoading = false;
          console.error('[PhoneVerification] Verify code error:', error);
          
          // Extract error message from various possible formats
          let errorMessage = 'Invalid verification code. Please try again.';
          let errorTitle = 'Verification Failed';
          
          if (error?.error) {
            if (error.error.message) {
              errorMessage = error.error.message;
            }
            if (error.error.code) {
              errorTitle = error.error.code.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
            }
          } else if (error?.message) {
            errorMessage = error.message;
          }
          
          // Display error inline
          this.errorMessage = errorMessage;
          

          // Clear the code field
          this.verificationForm.patchValue({ code: '' });
        }
      );
  }

  /**
   * Resend verification code
   */
  resendCode(): void {
    if (!this.canResend) {
      return;
    }

    this.isLoading = true;
    this.phoneVerificationService.sendVerificationCode(this.phoneNumber, this.selectedChannel, this.selectedCountryCode, this.getSelectedCountry()?.dialCode)
      .subscribe(
        (response) => {
          this.isLoading = false;
          this.canResend = false;
          this.startCountdown();
        },
        (error) => {
          this.isLoading = false;
          this.errorMessage = 'Failed to resend code. Please try again.';
        }
      );
  }

  /**
   * Start countdown timer for resend button
   */
  private startCountdown(): void {
    this.countdown = 60; // 60 seconds
    this.canResend = false;

    this.countdownInterval = setInterval(() => {
      this.countdown--;
      
      if (this.countdown <= 0) {
        this.stopCountdown();
        this.canResend = true;
      }
    }, 1000);
  }

  /**
   * Stop countdown timer
   */
  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  /**
   * Change phone number (go back to phone step)
   */
  changePhoneNumber(): void {
    this.step = 'phone';
    this.verificationForm.patchValue({ code: '' });
    this.stopCountdown();
    this.canResend = false;
  }

  /**
   * Select verification channel
   */
  selectChannel(channel: 'sms' | 'whatsapp'): void {
    this.selectedChannel = channel;
  }

  /**
   * Cancel verification
   */
  cancel(): void {
    this.stopCountdown();
    this.dialogRef.close({ verified: false });
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }
}

